import { getOptions, resolveLookupOptions } from '@ourbranch/lookups';
import { policyTypes as policyTypeCodes } from '@ourbranch/policy-types';
import uniqBy from 'lodash-es/uniqBy';
import { brandName } from 'brand-config';

/**
 * getOptions with the build-time brand injected as the carrier, so imperative call sites never
 * pass or hardcode a brand. Mirrors the carrier resolution done by the useOptions hook, so
 * brand-aware lookups behave the same whether resolved in a hook or imperatively.
 *
 * @param {string} name lookup name
 * @param {string} state 2-letter state
 * @param {string} [policyType] optional policyType sub-key
 */
export const getBrandOptions = (name, state, policyType) => getOptions(name, state, undefined, policyType, brandName);

/**
 * Whether a car's primary use is one the build-time brand doesn't offer in the car's garaging state:
 * the brand resolves its own list there (primaryUseByCarrier — CFM's MO list has no Commuting) and the
 * value isn't on it. A brand that resolves to the same list as no carrier (the base Pleasure / Business /
 * Commuting list) is never flagged. Uses the same resolver as the rater's getOptions, so the rater's
 * prefill and this gate can't disagree on what a brand offers. #37551
 *
 * @param {string|null|undefined} primaryUse the car's primary use id
 * @param {string|null|undefined} state the car's garaging state
 */
export const isPrimaryUseNotOfferedByBrand = (primaryUse, state) => {
  if (!primaryUse || !state) {
    return false;
  }
  const brandPrimaryUses = resolveLookupOptions('primaryUse', state, brandName);
  if (brandPrimaryUses === resolveLookupOptions('primaryUse', state)) {
    return false;
  }
  return !brandPrimaryUses.some(({ id }) => id === primaryUse);
};

export const getUmbrellaSuffix = ({ policyType, policyDetails } = {}) =>
  [policyTypeCodes.Home, policyTypeCodes.Condo].includes(policyType) && policyDetails?.includeUmbrella
    ? ' & Umbrella'
    : '';

export const contactType = [
  { id: 'personal', value: 'Personal' },
  { id: 'work', value: 'Work' },
  { id: 'contact', value: 'Contact' }
];

export const policyTypes = [
  { id: 'A', value: 'Auto' },
  { id: 'H', value: 'Home' },
  { id: 'R', value: 'Renters' },
  { id: 'C', value: 'Condo' },
  { id: 'F', value: 'Farm' },
  // Forreston/farm migration policy types: Z = farm second-home, Y = farm rental. Without these the
  // policy header rendered "undefined Policy" for migrated farm policies. #33934
  { id: 'Z', value: 'Home' },
  { id: 'Y', value: 'Rental' }
];

export const homeMultiPolicyDiscountOptions = [
  {
    id: 'N',
    value: 'None'
  },
  {
    id: 'S',
    value: 'Second Home'
  },
  {
    id: 'A',
    value: 'Auto'
  },
  {
    id: 'AR',
    value: 'Auto + Renters'
  },
  {
    id: 'ARS',
    value: 'Auto + Renters + Second Home'
  },
  {
    id: 'ARU',
    value: 'Auto + Renters + Umbrella'
  },
  {
    id: 'ARUS',
    value: 'Auto + Renters + Umbrella + Second Home'
  },
  {
    id: 'AS',
    value: 'Auto + Second Home'
  },
  {
    id: 'AU',
    value: 'Auto + Umbrella'
  },
  {
    id: 'AUS',
    value: 'Auto + Umbrella + Second Home'
  }
];

export const autoMultiPolicyDiscountOptions = [
  {
    id: 'N',
    value: 'None'
  },
  {
    id: 'R',
    value: 'Renters'
  },
  {
    id: 'HRU',
    value: 'Home + Renters + Umbrella'
  },
  {
    id: 'HR',
    value: 'Home + Renters'
  },
  {
    id: 'HU',
    value: 'Home + Umbrella'
  },
  {
    id: 'H',
    value: 'Home'
  },
  {
    id: 'CRU',
    value: 'Condo + Renters + Umbrella'
  },
  {
    id: 'CR',
    value: 'Condo + Renters'
  },
  {
    id: 'CU',
    value: 'Condo + Umbrella'
  },
  {
    id: 'C',
    value: 'Condo'
  }
];

export const autoMultiPolicyDiscountOptionsForOffer = [
  {
    id: 'N',
    value: 'None'
  },
  {
    id: 'H',
    value: 'Home'
  },
  {
    id: 'C',
    value: 'Condo'
  }
];

export const homeMultiPolicyDiscountOptionsForOffer = [
  {
    id: 'N',
    value: 'None'
  },
  {
    id: 'A',
    value: 'Auto'
  },
  {
    id: 'S',
    value: 'Second Home'
  },
  {
    id: 'AS',
    value: 'Auto + Second Home'
  }
];

/**
 *
 * @param {array} type one of the above list of types
 *
 * @returns {function} receives an Id and returns the value
 */
export const get = (type) => (id) => (type.find((item) => item.id === id) || {}).value;

export const formattedAffinityOptions = (affinityLookups) => {
  const affinityOptions = affinityLookups.data
    .filter((partner) => !partner.data.hideFromOffers)
    .map((partner) => {
      return {
        id: partner.affinity,
        text: [partner.name, partner.affinity, partner.data.agentName].filter(Boolean).join(' - '),
        affinityCode: partner.affinity
      };
    });

  return uniqBy(affinityOptions, (affinity) => affinity.id);
};
